import { Flex, useToast, ContextMenuItem, Text } from '@cad-arc';
import { ActionTypeEnum, UnitQueueView, UnitTimerResource } from '@mark43/cad-api';
import React from 'react';
import { createApiErrorHandler, useApiClient } from '@m43/core';
import { errorOccurredWhile } from '../common/helpers/error-occurred-while';
import { ErrorBoundary } from '../monitoring/error-boundary';
import { useFullscreenModalControls } from '../modals/context/modal-context-provider';

export const UnitTimersContextMenu = ({ data }: { data: UnitQueueView }): React.ReactElement => {
    return (
        <ErrorBoundary
            name="UNIT_TIMERS_CONTEXT_MENU"
            messageVerbNoun="opening the unit timer menu"
            asToast={true}
        >
            <UnitTimersContextMenuItems unitQueueView={data} />
        </ErrorBoundary>
    );
};

export const UnitTimersContextMenuItems: React.FC<{ unitQueueView: UnitQueueView }> = ({
    unitQueueView,
}) => {
    const apiClient = useApiClient();
    const modalControls = useFullscreenModalControls();
    const toast = useToast();

    const resetTimer = React.useCallback(() => {
        return apiClient.request<UnitTimerResource.Update2>({
            method: 'POST',
            url: 'unit_timer/',
            data: {
                actionType: ActionTypeEnum.RESET_UNIT_TIMER.name,
                unitId: unitQueueView.id,
                agencyEventId: unitQueueView.eventQueueView?.id,
            },
        });
    }, [apiClient, unitQueueView]);

    const clearTimer = React.useCallback(() => {
        return apiClient.request<UnitTimerResource.Update2>({
            method: 'POST',
            url: 'unit_timer/',
            data: {
                actionType: ActionTypeEnum.CLEAR_UNIT_TIMER.name,
                unitId: unitQueueView.id,
                agencyEventId: unitQueueView.eventQueueView?.id,
            },
        });
    }, [apiClient, unitQueueView]);

    return (
        <>
            <ContextMenuItem
                onSelect={() =>
                    resetTimer().catch(
                        createApiErrorHandler(() => {
                            toast({
                                status: 'error',
                                description: errorOccurredWhile('resetting timer'),
                            });
                        })
                    )
                }
            >
                <Flex justifyContent="space-between" w="100%">
                    <Text>Reset Timer</Text>
                    <Text>
                        {typeof unitQueueView.timerDefaultLength !== 'undefined'
                            ? `${unitQueueView.timerDefaultLength}M`
                            : ''}
                    </Text>
                </Flex>
            </ContextMenuItem>
            <ContextMenuItem
                onSelect={() => {
                    modalControls.open({
                        type: 'UNIT_TIMERS_RESET_TO',
                        props: { unitId: unitQueueView.id },
                    });
                }}
            >
                Reset Timer To...
            </ContextMenuItem>
            <ContextMenuItem
                onSelect={() =>
                    clearTimer().catch(
                        createApiErrorHandler(() => {
                            toast({
                                status: 'error',
                                description: errorOccurredWhile('clearing timer'),
                            });
                        })
                    )
                }
            >
                Clear Timer
            </ContextMenuItem>
        </>
    );
};
